import { Component, OnInit } from '@angular/core';
import { map } from 'rxjs/operators';
import { SegmentInformationDto } from '../../../dto/segment.information.dto';
import { SegmentService } from '../../../services/segment.service';
import { SegmentSubjectService } from '../../../subject/segment.subject';
import { SegmentPropertyDto } from '../../../dto/segment.property.dto';
import { PropertyInitialSubject } from '../../../subject/property.initial.subject';
import { PropertyService } from '../../../services/property.service';
import { MessageService } from 'primeng/api';
import { FlowMode } from '../../../dto/enum/flow.mode.enum';
import { PropertyInitialResponseDto } from '../../../dto/property.initial.response.dto';
import { PublishFlowFacade } from '../../../facades/publish-flow.facade';

@Component({
    selector: 'app-property-segments',
    templateUrl: './property-segments.component.html',
    styleUrls: ['./property-segments.component.scss'],
})
export class PropertySegmentsComponent implements OnInit {
    private readonly step = 'segments';
    mode: FlowMode = FlowMode.CREATE;
    isEditMode = false;
    isResumeMode = false;
    property: PropertyInitialResponseDto | null = null;

    nextUrl = '';
    previousUrl = '';

    segmentPropertyDtos: SegmentPropertyDto[] = [];
    selectedSegment: SegmentInformationDto | null = null;
    selectedSegmentId: number | null = null;

    constructor(
        private readonly segmentService: SegmentService,
        private readonly segmentSubjectService: SegmentSubjectService,
        private readonly propertyInitialSubject: PropertyInitialSubject,
        private readonly propertyService: PropertyService,
        private readonly messageService: MessageService,
        private readonly flow: PublishFlowFacade,
    ) {}

    ngOnInit(): void {
        this.flow.initStep(this.step);

        this.mode = this.flow.mode!;
        this.isEditMode = this.flow.isEditMode;
        this.isResumeMode = !this.isEditMode && !!this.propertyInitialSubject.getCurrentProperty();

        this.property = this.propertyInitialSubject.getCurrentProperty();
        if (this.property) {
            this.flow.setPropertyId(this.property.propertyId);
        }

        this.nextUrl = this.flow.nextUrl(this.step);
        this.previousUrl = this.flow.previousUrl(this.step);

        if (this.property) {
            this.getPropertySegmentData();
        } else {
            this.loadSegments();
        }
    }

    /** Selecciona una propiedad y actualiza el estado. */
    public selectItem(index: number): void {
        this.clearSelection();
        this.markSelectedProperty(index);
        this.updateSubjectWithSelectedProperty(index);
        const segment = this.segmentPropertyDtos[index];
        if (this.isEditMode) {
            this.updateSegment(segment.id);
        }
    }

    private clearSelection(): void {
        this.segmentPropertyDtos.forEach((item) => (item.selected = false));
    }

    private markSelectedProperty(index: number): void {
        const selectedProperty = this.segmentPropertyDtos[index];
        if (!selectedProperty) {
            return;
        }
        selectedProperty.selected = true;
    }

    private updateSubjectWithSelectedProperty(index: number): void {
        const selectedProperty = this.segmentPropertyDtos[index];

        this.selectedSegment = {
            segmentId: selectedProperty.id,
            segmentCode: selectedProperty.code,
            segmentLabel: selectedProperty.name,
            description: selectedProperty.description,
            fileUrl: selectedProperty.imageUrl,
        };

        this.segmentSubjectService.updateSelectedSegment(this.selectedSegment);
        this.flow.setStepData(this.step, this.selectedSegment);
    }

    private loadSegments(): void {
        this.segmentService.getSegmentList()
            .pipe(map(this.mapSegmentInformationToPropertyDto))
            .subscribe({
                next: (segments) => {
                    this.segmentPropertyDtos = segments;
                    this.handleSegmentSelectionAfterLoad();
                },
                error: (err) => console.error('Error al cargar propiedades:', err),
            });
    }

    private mapSegmentInformationToPropertyDto(segments: SegmentInformationDto[]): SegmentPropertyDto[] {
        return segments.map((segment) => ({
            id: segment.segmentId,
            code: segment.segmentCode,
            name: segment.segmentLabel,
            description: segment.description,
            selected: false,
            imageUrl: segment.fileUrl,
        }));
    }

    private handleSegmentSelectionAfterLoad(): void {
        const cachedSegment = this.flow.getStepData<SegmentInformationDto | null>(this.step);

        if (cachedSegment) {
            const index = this.segmentPropertyDtos.findIndex((p) => p.id === cachedSegment.segmentId);
            if (index !== -1) {
                this.selectItem(index);
                return;
            }
        }

        if (this.isEditMode && this.selectedSegmentId != null) {
            const index = this.segmentPropertyDtos.findIndex((p) => p.id === this.selectedSegmentId);
            if (index !== -1) {
                this.selectItem(index);
            }
        }
    }

    private getPropertySegmentData(): void {
        this.propertyService.getPropertyAddress(this.property?.propertyId!).subscribe({
            next: (response) => {
                this.selectedSegmentId = response?.segmentId || null;
                this.loadSegments();
            },
            error: (error) => {
                console.error('Error al obtener segmento:', error);
                this.loadSegments();
            },
        });
    }

    private updateSegment(segmentId: number): void {
        if (!this.property) {
            return;
        }
        if (this.selectedSegmentId !== segmentId) {
            return;
        }

        this.propertyService.updatePropertySegment(this.property.propertyId, segmentId).subscribe(
            (response) => {
                this.property = response.propertyId;
                this.propertyInitialSubject.updateProperty(this.property!);
                this.flow.setPropertyId(this.property?.propertyId!);
                this.flow.goTo(this.nextUrl);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Segmento actualizado',
                    detail: response?.message || 'El anuncio ha sido actualizada correctamente.',
                    life: 4000,
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error?.error?.message || 'No se pudo actualizar el segmento.',
                    life: 5000,
                });
            },
        );
    }

    handlePreviousStep(): void {
        this.flow.goTo(this.previousUrl);
    }

    handleNextStep(): void {
        if (!this.selectedSegment) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Aviso',
                detail: 'Debes seleccionar un tipo de propiedad para continuar.',
                life: 3000,
            });
            return;
        }
        this.flow.goTo(this.nextUrl);
    }
}
